import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import api from 'services/api';

import { ReactComponent as EditPencil } from 'assets/icons/edit-pencil.svg';
import { ReactComponent as LeftArrow } from 'assets/icons/left-arrow.svg';
import { ReactComponent as QuestionCircle } from 'assets/icons/question-circle.svg';
import { ReactComponent as StarCircle } from 'assets/icons/star-circle.svg';

import { Select } from 'shared/components/molecules/Select/Select';
import { Header } from 'shared/components/organisms/Header/Header';
import { Sidebar } from 'shared/components/organisms/Sidebar/Sidebar';

import { Container, Content, ReturnButton } from './_services';
import { DifficultyPriceModal } from 'shared/components/molecules/DifficultyPriceModal/DifficultyPriceModal';
import { NotificationModal } from 'shared/components/organisms/NotificationModal/NotificationModal';
import { ProductListModal } from 'shared/components/organisms/ProductListModal/ProductListModal';
import useAuth from 'hooks/UseAuth';
import { getProductNameFromServiceId } from 'utils/getProductNameFromServiceId';

interface IServiceTypeDifficulty {
  id: number;
  name: string;
  default_price: number;
  difficulty: number;

  formattedDefaultPrice: string;
  isDefault?: boolean;
}

interface IServiceType {
  id: number;
  name: string;
  description: string;
  base_price: number;
  is_active: boolean;
  order: number;
}

interface IServiceTypeDetails extends IServiceType {
  service_type_difficulties: IServiceTypeDifficulty[];
}

interface IProviderDifficulty {
  difficulty: number | string;
  price: number;

  formattedPrice: string;
}

export const Services = () => {
  const { user } = useAuth();
  const [serviceTypes, setServiceTypes] = useState<IServiceType[]>([]);
  const [selectedServiceType, setSelectedServiceType] =
    useState<IServiceTypeDetails>();
  const [selectedServiceOption, setSelectedServiceOption] = useState<{
    value: string;
    label: string;
  }>({
    value: '',
    label: '',
  });
  const [pricePerKm, setPricePerKm] = useState(0);
  const [selectedDifficulty, setSelectedDifficulty] =
    useState<IServiceTypeDifficulty>();
  const [selectedProductDifficulty, setSelectedProductDifficulty] =
    useState<IServiceTypeDifficulty>();
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [providerDifficulties, setProviderDifficulties] = useState<
    IProviderDifficulty[]
  >([]);

  useEffect(() => {
    getUserServiceTypes();
  }, [user]);

  useEffect(() => {
    fetchProviderDifficulties();
  }, [selectedServiceOption]);

  useEffect(() => {
    if (selectedServiceOption.value) {
      fetchServiceDetails();
    } else {
      setSelectedServiceType(undefined);
    }
  }, [selectedServiceOption]);

  useEffect(() => {
    if(selectedServiceType) {
      ensurePricePerKm();
    }
  }, [selectedServiceType]);

  const getUserServiceTypes = async () => {
    try {
      setServiceTypes(
        user?.provider?.service_types.length
          ? user?.provider?.service_types
          : [],
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const ensurePricePerKm = async () => {
    try {
      const response = await api.get<{
        price: number;
      }>(`/price/frete`, {
        params: {
          categoryId: Number(selectedServiceType?.id || '1'),
        }
      });

      setPricePerKm(response?.data?.price || 0)
    } catch (error: any) {
      if (error?.response && error?.response.status === 400) {
        console.log('Prestador ainda não possui preço de frete');
      } else {
        toast.error(error?.response?.data?.message || 'Algo deu errado, tente novamente mais tarde');
      }
    }
  }

  const fetchProviderDifficulties = async () => {
    try {
      const response = await api.get<IProviderDifficulty[]>('/price', {
        params: {
          categoryId: selectedServiceOption?.value,
        },
      });

      setProviderDifficulties(
        response.data.map((providerDifficulty) => ({
          ...providerDifficulty,
          formattedPrice: new Intl.NumberFormat('pt-BR', {
            currency: 'BRL',
            style: 'currency',
          }).format(providerDifficulty.price),
        })),
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const fetchServiceDetails = async () => {
    try {
      const response = await api.get<IServiceTypeDetails>(
        `/service-types/${selectedServiceOption?.value}`,
      );

      const service = response.data;

      const defaultDifficulties = await fetchDefaultDifficulties(
        selectedServiceOption?.value,
      );

      setSelectedServiceType({
        ...service,
        service_type_difficulties: defaultDifficulties
          ? defaultDifficulties.map((difficulty) => ({
              ...difficulty,
              formattedDefaultPrice: new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(difficulty.default_price / 100),
            }))
          : [],
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const fetchDefaultDifficulties = async (categoryId: string) => {
    try {
      const response = await api.get<IServiceTypeDifficulty[]>(
        `/products/prices`,
        {
          params: {
            categoryId,
          },
        },
      );

      return response.data
        .sort((a, b) => a.difficulty - b.difficulty)
        .map((difficulty) => ({
          ...difficulty,
          formattedDefaultPrice: new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(difficulty.default_price / 100),
        }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const handleDifficultyPriceConfirm = async (
    updatedDifficulty: IServiceTypeDifficulty,
    price: number,
  ) => {
    try {
      const response = await api.post<IProviderDifficulty>('/price', {
        providerId: user?.provider?.id,
        price: price * 100,
        difficulty: Number(updatedDifficulty.difficulty),
      });
      setProviderDifficulties((prevState) => {
        if (
          prevState.find(
            (providerDifficulty) =>
              providerDifficulty.difficulty ===
              Number(updatedDifficulty.difficulty),
          )
        ) {
          return prevState.map((providerDifficulty) => {
            if (
              providerDifficulty.difficulty ===
              Number(updatedDifficulty.difficulty)
            ) {
              return {
                ...providerDifficulty,
                price: price * 100,
                formattedPrice: new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(price * 100),
              };
            }

            return providerDifficulty;
          });
        }

        return [...prevState, response.data];
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const selectOptions = serviceTypes.map((category) => ({
    value: category.id.toString(),
    label: category.name,
  }));

  const prices = selectedServiceType?.service_type_difficulties.map(
    (difficulty) => {
      const providerHasDifficulty = providerDifficulties.find(
        (providerDifficulty) =>
          providerDifficulty.difficulty === Number(difficulty.difficulty),
      );

      if (!providerHasDifficulty) return { ...difficulty, isDefault: true };

      return {
        ...difficulty,
        isDefault: difficulty.default_price === providerHasDifficulty.price,
        default_price: providerHasDifficulty.price,
        formattedDefaultPrice: new Intl.NumberFormat('pt-BR', {
          currency: 'BRL',
          style: 'currency',
        }).format(providerHasDifficulty.price / 100),
      };
    },
  );

  return (
    <Container>
      <Header />
      <Sidebar />
      <Content>
        <ReturnButton to="/">
          <LeftArrow className="services__content-icon" />
        </ReturnButton>
        <div className="services__wrapper">
          <h1 className="services__title">Tabela de preços e serviços</h1>

          <div className="services__information-container">
            <h2 className="services__subtitle">
              Precifique seus serviços aqui
            </h2>
            <p className="services__text">
              Aqui você pode criar sua própria tabela de preços, ou usar a
              tabela sugerida pela Repfy.
            </p>
          </div>

          <div className="services__select-category-container">
            <h2 className="services__subtitle">
              Selecione a categoria de serviços
            </h2>
            <Select
              options={selectOptions}
              selectedItem={selectedServiceOption}
              setSelectedItem={setSelectedServiceOption}
              className="services__select"
            />
          </div>
          {selectedServiceType && (
            <>
              <div className="services__input-wrapper">
                <label className="services__label">Preço por quilômetro:</label>
                <div className="services__prefix-input-container">
                  <span className="services__prefix">
                  {
                      new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(pricePerKm / 100)
                    }
                  </span>
                </div>
              </div>
              <div className="services__price-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Grau de complexidade</th>
                      <th>Preço sugerido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices &&
                      prices.length &&
                      prices.map((price) => (
                        <tr key={`difficulty-${price.id}`}>
                          <td>
                            {price.difficulty}
                            <button
                              type="button"
                              className="services__table-question-button"
                              onClick={() =>
                                setSelectedProductDifficulty(price)
                              }
                              data-tooltip={`Ver lista de ${getProductNameFromServiceId(
                                Number(selectedServiceOption.value),
                              )} que se encaixam nesse grau de complexidade`}
                            >
                              <QuestionCircle />
                            </button>
                          </td>
                          <td>
                            <div className="services__table-second-col-container">
                              {price.formattedDefaultPrice}
                              {price.isDefault && <StarCircle />}
                              <button
                                type="button"
                                className="services__table-edit-button"
                                onClick={() => setSelectedDifficulty(price)}
                              >
                                <EditPencil />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
        {!!selectedDifficulty && (
          <DifficultyPriceModal
            difficulty={selectedDifficulty}
            toggleModal={() => setSelectedDifficulty(undefined)}
            serviceName={selectedServiceType?.name || ''}
            onConfirm={handleDifficultyPriceConfirm}
          />
        )}
        {showNotificationModal && (
          <NotificationModal toggleModal={setShowNotificationModal} success />
        )}
        {!!selectedProductDifficulty && (
          <ProductListModal
            difficulty={selectedProductDifficulty}
            toggleModal={() => setSelectedProductDifficulty(undefined)}
            serviceTypeId={selectedServiceOption.value}
          />
        )}
      </Content>
    </Container>
  );
};
